import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LuTrash2,
  LuRefreshCw,
  LuTriangleAlert,
  LuMail,
  LuMessageSquare,
  LuPaperclip,
  LuBot,
  LuHardDrive,
} from "react-icons/lu";
import type { ComponentType } from "react";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";

function bytesToHuman(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function CleanupPage() {
  const qc = useQueryClient();
  const [graceMinutes, setGraceMinutes] = useState(0);

  const preview = useQuery({
    queryKey: ["console", "cleanup", "preview"],
    queryFn: () => api.cleanup.preview(),
    refetchInterval: 60_000,
  });

  const run = useMutation({
    mutationFn: (mins: number) => api.cleanup.run(mins),
    onSuccess: (res) => {
      toast.success("Cleanup complete", {
        description: `Removed ${res.expiredInboxesDeleted} inboxes, ${res.messagesDeleted} messages, ${res.r2ObjectsDeleted} attachments in ${res.durationMs}ms.`,
      });
      qc.invalidateQueries({ queryKey: ["console", "cleanup", "preview"] });
      qc.invalidateQueries({ queryKey: ["console", "admin", "metrics"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Cleanup failed");
    },
  });

  function handleRun() {
    const expectedInboxes = preview.data?.inboxes ?? 0;
    if (expectedInboxes === 0) {
      toast.info("Nothing to clean.");
      return;
    }
    const ok = window.confirm(
      `Permanently delete ${expectedInboxes.toLocaleString()} expired inboxes` +
        ` and their ${preview.data?.messages.toLocaleString() ?? 0} messages?` +
        ` Grace period: ${graceMinutes} min. This cannot be undone.`,
    );
    if (!ok) return;
    run.mutate(graceMinutes);
  }

  return (
    <>
      <PageHeader
        icon={LuTrash2}
        title="Data cleanup"
        description="Delete expired inboxes and their messages, attachments, and agent identities. Runs nightly automatically; use this to force an immediate sweep."
        actions={
          <Button
            variant="secondary"
            onClick={() => preview.refetch()}
            disabled={preview.isFetching}
          >
            <LuRefreshCw
              className={`w-4 h-4 ${preview.isFetching ? "animate-spin" : ""}`}
            />
            Refresh preview
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {preview.isLoading && (
          <p className="text-sm text-[var(--color-text-muted)]">
            Counting expired records...
          </p>
        )}

        {preview.data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <Stat
                icon={LuMail}
                label="Expired inboxes"
                value={preview.data.inboxes.toLocaleString()}
              />
              <Stat
                icon={LuMessageSquare}
                label="Messages"
                value={preview.data.messages.toLocaleString()}
              />
              <Stat
                icon={LuPaperclip}
                label="Attachments"
                value={preview.data.attachments.toLocaleString()}
              />
              <Stat
                icon={LuBot}
                label="Agent identities"
                value={preview.data.agentIdentities.toLocaleString()}
              />
              <Stat
                icon={LuHardDrive}
                label="R2 storage"
                value={bytesToHuman(preview.data.r2StorageBytes)}
              />
            </div>

            <Card>
              <div className="px-5 py-4 border-b border-[var(--color-border)]">
                <h2 className="text-sm font-semibold text-[var(--color-text)]">
                  Run cleanup
                </h2>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  Inboxes whose `expires_at` is older than the grace period are
                  deleted along with their messages, attachment records, R2
                  objects, and agent identities.
                </p>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                      Grace period (minutes)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={1440}
                      value={graceMinutes}
                      onChange={(e) =>
                        setGraceMinutes(
                          Math.max(0, Math.min(1440, Number(e.target.value) || 0)),
                        )
                      }
                      className="w-32 px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
                    />
                    <p className="text-[10px] text-[var(--color-text-subtle)] mt-1">
                      0 = immediate. Max 1440 (1 day).
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    onClick={handleRun}
                    disabled={run.isPending || preview.data.inboxes === 0}
                  >
                    <LuTrash2 className="w-4 h-4" />
                    {run.isPending ? "Cleaning..." : "Run cleanup now"}
                  </Button>
                </div>

                <div className="flex items-start gap-2 p-3 rounded-md bg-[rgba(250,176,5,0.06)] border border-[rgba(250,176,5,0.25)]">
                  <LuTriangleAlert className="w-4 h-4 text-[#fcd34d] shrink-0 mt-0.5" />
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Destructive. Removed records can't be restored. The cron
                    runs this every night with a 60-minute grace period.
                  </p>
                </div>
              </div>
            </Card>

            {preview.data.sampleInboxes.length > 0 && (
              <Card>
                <div className="px-5 py-4 border-b border-[var(--color-border)]">
                  <h2 className="text-sm font-semibold text-[var(--color-text)]">
                    Sample of what would be removed
                  </h2>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    First {preview.data.sampleInboxes.length} expired inboxes
                    by oldest expiry.
                  </p>
                </div>
                <div className="divide-y divide-[var(--color-border)]">
                  {preview.data.sampleInboxes.map((i) => (
                    <div
                      key={i.id}
                      className="px-5 py-3 flex items-center justify-between gap-4 text-xs"
                    >
                      <span className="font-mono text-[var(--color-text)] truncate">
                        {i.address}
                      </span>
                      <span className="text-[var(--color-text-subtle)] font-mono tabular-nums">
                        expired {new Date(i.expires_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-[var(--color-text-subtle)]" />
        <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
          {label}
        </p>
      </div>
      <p className="text-lg font-semibold text-[var(--color-text)] tabular-nums">
        {value}
      </p>
    </div>
  );
}
